import { RECEIVED_KINDS, RECEIVED_KIND_LABEL, RECEIVED_MSG, receivedKindOption, type ReceivedDocument, type ReceivedKind, type ReceivedPages, type ReceivedText } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { api, messageOf } from "../../api.js";
import { Trunc } from "../../components/GroupList.jsx";
import { RowMenu } from "../../components/RowMenu.jsx";
import { useNarrow } from "../../lib/useNarrow.js";
import type { ToastFn } from "../../components/ui.jsx";

/** 原本をダウンロードする（⋮ の項目から。a[download] を押したのと同じ） */
function download(url: string): void {
  const a = document.createElement("a");
  a.href = url;
  a.download = "";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * 受け取った書類の引き出し（../eigyouryoku/docs/13 §4.1）。右から・画面幅いっぱい（{vendorQuote}の原本 VendorOriginal と同じ作り＝.vdrawer）。
 *   開くたびに書類を読み直す（GET /received-documents/:id。一覧で種類を変えたあとに開いても古い種類が残らない、S1）。
 *   上の帯：
 *     PC（768px〜）＝1段：名前・種類・「ダウンロード」「削除」「閉じる」
 *     幅 767px 以下＝2段（docs/10 §9.20）：1段目＝名前（1行・「…」・押すと全部＝Trunc）と「閉じる」／2段目＝種類のプルダウン（一覧と同じ PATCH）と「⋮」（ダウンロード・削除）
 *     ボタンは縮めず折り返さない（言葉の途中で切らない）。入らない動きは ⋮ へ
 *   中身：
 *     PDF＝PC は <iframe>（原本の応答だけ API が SAMEORIGIN を付ける＝#87 と同じ）。幅 767px 以下はページ画像を縦に並べ、上に「原本表示」（新しいタブ＝Safari の PDF 表示）
 *     TIFF＝どの幅でもページ画像（ブラウザは TIFF を表示できない）。「ダウンロード」は原本の TIFF
 *     ほかの画像＝表示用の <img>（押すと原本）／文字＝<pre>
 *     ページ画像は初めて開いたときに API が作る（S2）＝できるまで「準備中…」（薄墨）
 *   Esc で閉じる。開いたら枠にフォーカス（PDF の中にフォーカスが入ると Esc が届かない）
 */
export function ReceivedViewer({
  doc,
  onClose,
  onDelete,
  onChanged,
  toast,
}: {
  doc: ReceivedDocument | null;
  onClose: () => void;
  onDelete: (d: ReceivedDocument) => void;
  /** 種類を変えた（一覧を読み直す） */
  onChanged: () => void;
  toast: ToastFn;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  const narrow = useNarrow();
  const id = doc?.id;
  // 開くたびに読み直す：閉じると鍵（id）が外れて控えは消える（gcTime 0）＝次に開くと新しく読む。読み終わるまで種類は出さない
  const fresh = useQuery({
    queryKey: ["received-doc", id],
    queryFn: () => api<ReceivedDocument>("GET", `/received-documents/${id}`),
    enabled: !!id,
    gcTime: 0,
    staleTime: 0,
  });
  const cur = fresh.data && fresh.data.id === id ? fresh.data : null;
  const d = cur ?? doc;
  useEffect(() => {
    if (!doc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // 上に重なった窓（削除の確かめ）・⋮ のメニューの Esc はそちらに任せる
      if (document.querySelector(".dim") || document.querySelector(".pop.pop-drawer")) return;
      e.stopPropagation();
      onClose();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [doc, onClose]);
  useEffect(() => {
    if (doc) ref.current?.focus();
  }, [doc?.id]);

  const changeKind = async (kind: ReceivedKind) => {
    if (!cur) return;
    try {
      const next = await api<ReceivedDocument>("PATCH", `/received-documents/${cur.id}`, { kind });
      qc.setQueryData(["received-doc", cur.id], next);
      onChanged();
      toast(RECEIVED_MSG.kindToast.replace("{kind}", RECEIVED_KIND_LABEL[kind]));
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  const kindSelect = (
    <select
      className={"inp rdkind" + (cur?.kind === "unsorted" ? " is-unsorted" : "")}
      value={cur?.kind ?? ""}
      disabled={!cur || !!cur.deletedAt}
      aria-label={`${d?.title ?? ""} の${RECEIVED_MSG.col.kind}`}
      onChange={(e) => void changeKind(e.target.value as ReceivedKind)}
      data-testid="received-viewer-kind"
    >
      {cur ? null : <option value="" />}
      {RECEIVED_KINDS.map((k) => (
        <option key={k} value={k}>
          {receivedKindOption(k)}
        </option>
      ))}
    </select>
  );
  const closeBtn = (
    <button type="button" className="btn" onClick={onClose} data-testid="received-viewer-close">
      {RECEIVED_MSG.close}
    </button>
  );

  return (
    <div ref={ref} tabIndex={-1} className={"vdrawer rdviewer" + (doc ? " is-open" : "")} role="dialog" aria-label={d?.title ?? RECEIVED_MSG.title} aria-hidden={!doc} data-testid="received-viewer">
      {narrow ? (
        <div className="dh rdv-dh2">
          <div className="rdv-row">
            {/* 書類の名前は「値」（1行・「…」・押すと全部＝Trunc。docs/10 §9.20 d）。見出しの役目は role で */}
            <div className="rdv-name" role="heading" aria-level={2}>
              {d ? <Trunc key={d.id} testId="received-viewer-title">{d.title}</Trunc> : null}
            </div>
            {closeBtn}
          </div>
          <div className="rdv-row">
            {kindSelect}
            <span className="sp" />
            {d ? (
              <RowMenu
                label={`${d.title} の${RECEIVED_MSG.more}`}
                testId="received-viewer-more"
                popClass="pop-drawer"
                items={[
                  ...(d.downloadUrl ? [{ label: RECEIVED_MSG.download, onSelect: () => download(d.downloadUrl!), testId: "received-download" }] : []),
                  ...(!d.deletedAt ? [{ label: RECEIVED_MSG.delete, onSelect: () => onDelete(d), danger: true, testId: "received-viewer-delete" }] : []),
                ]}
              />
            ) : null}
          </div>
        </div>
      ) : (
        <div className="dh">
          <h2 className="rdv-title" title={d?.title}>
            {d?.title ?? ""}
          </h2>
          {cur ? <span className="chip" data-testid="received-viewer-chip">{RECEIVED_KIND_LABEL[cur.kind]}</span> : null}
          <span className="sp" />
          {d?.downloadUrl ? (
            <a className="btn text" href={d.downloadUrl} download data-testid="received-download">
              {RECEIVED_MSG.download}
            </a>
          ) : null}
          {d && !d.deletedAt ? (
            <button type="button" className="btn text rddel" onClick={() => onDelete(d)} data-testid="received-viewer-delete">
              {RECEIVED_MSG.delete}
            </button>
          ) : null}
          {closeBtn}
        </div>
      )}
      {d ? <ReceivedOriginal doc={d} /> : <div className="db" />}
    </div>
  );
}

/**
 * 原本の中身（引き出しの本文。原本の引き出しと、読み取りの確認の引き出し ReceivedReadDrawer の左が同じものを使う）。
 *   PDF＝PC は <iframe>（原本の応答だけ API が SAMEORIGIN）、幅 767px 以下（または pagesOnly）はページ画像＋「原本表示」
 *   TIFF＝どの幅でもページ画像（ブラウザは TIFF を表示できない）／ほかの画像＝表示用の <img>（押すと原本）／文字＝<pre>
 *   ページ画像は初めて開いたときに API が作る（S2）＝できるまで「準備中…」
 */
export function ReceivedOriginal({ doc: d, pagesOnly = false, className = "" }: { doc: ReceivedDocument; pagesOnly?: boolean; className?: string }) {
  const narrow = useNarrow();
  const id = d.id;
  const isTiff = d.contentType === "image/tiff";
  const showPages = isTiff || (d.format === "pdf" && (narrow || pagesOnly));
  const pages = useQuery({
    queryKey: ["received-pages", id],
    queryFn: () => api<ReceivedPages>("GET", `/received-documents/${id}/pages`),
    enabled: showPages,
    gcTime: 0,
  });
  const text = useQuery({
    queryKey: ["received-text", id],
    queryFn: () => api<ReceivedText>("GET", `/received-documents/${id}/text`),
    enabled: d.format === "text",
  });
  return (
    <div className={"db" + (showPages ? " rdv-scroll" : "") + (className ? ` ${className}` : "")}>
      {showPages ? (
        <div className="rdv-pages" data-testid="received-pages">
          {d.format === "pdf" && d.originalUrl ? (
            <a className="btn rdv-open" href={d.originalUrl} target="_blank" rel="noopener" data-testid="received-open-original">
              {RECEIVED_MSG.openOriginal}
            </a>
          ) : null}
          {pages.isError ? (
            <p className="shu">{messageOf(pages.error)}</p>
          ) : !pages.data ? (
            <p className="hint" data-testid="received-pages-preparing">
              {RECEIVED_MSG.preparing}
            </p>
          ) : pages.data.pages.length === 0 ? (
            <p className="hint">{RECEIVED_MSG.noPages}</p>
          ) : (
            pages.data.pages.map((p) => (
              <img
                key={p.n}
                className="rdv-page"
                src={p.url}
                alt={RECEIVED_MSG.pageAlt(d.title, p.n)}
                width={p.width ?? undefined}
                height={p.height ?? undefined}
                loading={p.n > 2 ? "lazy" : "eager"}
                data-testid="received-page"
              />
            ))
          )}
        </div>
      ) : d.format === "pdf" && d.originalUrl ? (
        <iframe key={d.originalUrl} src={`${d.originalUrl}#navpanes=0`} title={d.title} data-testid="received-view" />
      ) : d.format === "image" && d.originalUrl ? (
        <a className="rdv-img" href={d.originalUrl} target="_blank" rel="noreferrer" title={d.title}>
          <img src={d.displayUrl ?? d.originalUrl} alt={d.title} data-testid="received-view" />
        </a>
      ) : d.format === "text" ? (
        text.isError ? (
          <p className="shu">{messageOf(text.error)}</p>
        ) : (
          <pre className="rdv-text" data-testid="received-view">
            {text.data?.body ?? ""}
          </pre>
        )
      ) : (
        <p className="hint">{RECEIVED_MSG.noPreview}</p>
      )}
    </div>
  );
}
